// ─── Pagination ───────────────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  readonly data:       readonly T[];
  readonly total:      number;
  readonly page:       number;
  readonly limit:      number;
  readonly totalPages: number;
}

// ─── Problem Details (RFC 7807, matches backend ApiProblemDetails) ─────────────

export interface ApiProblemDetails {
  readonly type:          string;
  readonly title:         string;
  readonly status:        number;
  readonly detail:        string | readonly string[];
  readonly correlationId: string;
  readonly code?:         string;
  readonly field?:        string;
  readonly details?:      ReadonlyArray<{ field: string; issue: string }>;
}

export class ApiError extends Error {
  constructor(
    readonly status:  number,
    readonly code:    string | undefined,
    readonly detail:  string | readonly string[],
    readonly payload: ApiProblemDetails,
  ) {
    super(typeof detail === 'string' ? detail : detail.join('; '));
    this.name = 'ApiError';
  }
}

// ─── Identity ─────────────────────────────────────────────────────────────────

export type IdentityStatus      = 'ACTIVE' | 'SUSPENDED';
export type IdentityType        = 'HUMAN' | 'SERVICE' | 'SYSTEM';
export type ProvisioningStatus  = 'NOT_REQUIRED' | 'PENDING' | 'PROVISIONED' | 'FAILED';

export interface IdentityProfile {
  readonly id:                      string;
  readonly subjectId:               string;
  readonly email:                   string;
  readonly username:                string;
  readonly displayName:             string;
  readonly status:                  IdentityStatus;
  readonly identityType:            IdentityType;
  readonly provisioningStatus:      ProvisioningStatus;
  readonly preferredOrganizationId: string | null;
  readonly codeDeclarant:           string | null;
  readonly isBlocked:               boolean;
  readonly blockReason:             string | null;
  readonly phoneNumber:             string | null;
  readonly cine:                    string | null;
  readonly passportNumber:          string | null;
  readonly driverLicenseNumber:     string | null;
  readonly createdAt:               string;
  readonly updatedAt:               string;
  readonly links?:                  readonly IdentityLink[];
}

export interface IdentityLink {
  readonly id:              string;
  readonly identityId:      string;
  readonly provider:        string;
  readonly externalSubject: string;
  readonly displayName:     string | null;
  readonly createdAt:       string;
  readonly updatedAt:       string;
}

export interface RegisterIdentityRequest {
  readonly identityType?: IdentityType;
  readonly subjectId?:    string;
  readonly email:         string;
  readonly username?:     string;
  readonly displayName:   string;
  readonly codeDeclarant?: string | null;
  readonly isBlocked?: boolean;
  readonly blockReason?: string | null;
  readonly phoneNumber?: string | null;
  readonly cine?: string | null;
  readonly passportNumber?: string | null;
  readonly driverLicenseNumber?: string | null;
}

export interface UpdateIdentityRequest {
  readonly email:       string;
  readonly username?:    string | null;
  readonly displayName: string;
  readonly codeDeclarant?: string | null;
  readonly isBlocked?: boolean;
  readonly blockReason?: string | null;
  readonly phoneNumber?: string | null;
  readonly cine?: string | null;
  readonly passportNumber?: string | null;
  readonly driverLicenseNumber?: string | null;
}

export interface UpdateIdentityStatusRequest {
  readonly status: IdentityStatus;
}

export interface RegisterAccountRequest {
  readonly email: string;
  readonly displayName: string;
  readonly password: string;
}

export interface RegisterAccountResponse {
  readonly identityId: string;
  readonly subjectId: string;
  readonly realm: string;
  readonly email: string;
  readonly displayName: string;
  readonly createdAt: string;
}

export interface ResetIdentityPasswordRequest {
  readonly temporaryPassword: string;
}

export interface ResetIdentityPasswordResponse {
  readonly subjectId: string;
  readonly realm: string;
  readonly performed: boolean;
  readonly passwordUpdateRequiredOnNextLogin: boolean;
  readonly resetAt: string;
}

export interface IdentityAccount {
  readonly subjectId: string;
  readonly realm: string;
  readonly username: string | null;
  readonly email: string | null;
  readonly enabled: boolean;
}

// ─── Organization ─────────────────────────────────────────────────────────────

export type MembershipRole   = 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER';
export type MembershipStatus = 'PENDING' | 'ACTIVE' | 'REVOKED';

export interface Organization {
  readonly id:              string;
  readonly slug:            string;
  readonly displayName:     string;
  readonly ice:             string | null;
  readonly codeDouane:      string | null;
  readonly codeRc:          string | null;
  readonly codeCentreRc:    number;
  readonly identificationFiscale: string | null;
  readonly isBlocked:       boolean;
  readonly motifBlocage:    string | null;
  readonly dateCreation:    string | null;
  readonly dateEnregistrement: string | null;
  readonly dateBlocage:     string | null;
  readonly email:           string | null;
  readonly telephone:       string | null;
  readonly address:         string | null;
  readonly formeJuridique:  string | null;
  readonly isAgentMaritime: boolean;
  readonly ownerIdentityId: string;
  readonly createdAt:       string;
  readonly updatedAt:       string;
}

export interface OrganizationMembership {
  readonly id:             string;
  readonly organizationId: string;
  readonly identityId:     string;
  readonly role:           MembershipRole;
  readonly status:         MembershipStatus;
  readonly invitedById:    string | null;
  readonly joinedAt:       string | null;
  readonly createdAt:      string;
  readonly updatedAt:      string;
}

export interface OrganizationUnit {
  readonly id:             string;
  readonly organizationId: string;
  readonly parentUnitId:   string | null;
  readonly code:           string;
  readonly displayName:    string;
  readonly createdAt:      string;
  readonly updatedAt:      string;
}

export interface CreateOrganizationRequest {
  readonly slug:            string;
  readonly displayName:     string;
  readonly ice?:            string | null;
  readonly codeDouane?:     string | null;
  readonly codeRc?:         string | null;
  readonly codeCentreRc?:   number;
  readonly identificationFiscale?: string | null;
  readonly isBlocked?:      boolean;
  readonly motifBlocage?:   string | null;
  readonly dateCreation?:   string | null;
  readonly dateEnregistrement?: string | null;
  readonly dateBlocage?:    string | null;
  readonly email?:          string | null;
  readonly telephone?:      string | null;
  readonly address?:        string | null;
  readonly formeJuridique?: string | null;
  readonly isAgentMaritime?: boolean;
}

export interface CreateOrganizationResponse {
  readonly organization:   Organization;
  readonly ownerMembership: OrganizationMembership;
}

export interface UpdateOrganizationRequest {
  readonly displayName?:     string;
  readonly ice?:             string | null;
  readonly codeDouane?:      string | null;
  readonly codeRc?:          string | null;
  readonly codeCentreRc?:    number;
  readonly identificationFiscale?: string | null;
  readonly isBlocked?:       boolean;
  readonly motifBlocage?:    string | null;
  readonly dateCreation?:    string | null;
  readonly dateEnregistrement?: string | null;
  readonly dateBlocage?:     string | null;
  readonly email?:           string | null;
  readonly telephone?:       string | null;
  readonly address?:         string | null;
  readonly formeJuridique?:  string | null;
  readonly isAgentMaritime?: boolean;
}

export interface TransferOrganizationOwnershipRequest {
  readonly ownerIdentityId: string;
}

export interface TransferOrganizationOwnershipResponse {
  readonly organization: Organization;
  readonly ownerMembership: OrganizationMembership;
}

export interface AssignMembershipRequest {
  readonly identityId:         string;
  readonly role:               MembershipRole;
  readonly invitedById?:       string;
  readonly activateImmediately?: boolean;
}

export type OrganizationJoinRequestStatus = 'SUBMITTED' | 'APPROVED' | 'REJECTED';

export interface OrganizationCatalogEntry {
  readonly organizationId: string;
  readonly slug: string;
  readonly displayName: string;
  readonly ice: string | null;
  readonly codeDouane: string | null;
  readonly codeRc: string | null;
  readonly codeCentreRc: number;
  readonly identificationFiscale: string | null;
  readonly isBlocked: boolean;
  readonly motifBlocage: string | null;
  readonly dateCreation: string | null;
  readonly dateEnregistrement: string | null;
  readonly dateBlocage: string | null;
  readonly email: string | null;
  readonly telephone: string | null;
  readonly address: string | null;
  readonly formeJuridique: string | null;
  readonly isAgentMaritime: boolean;
  readonly ownerIdentityId: string;
  readonly isMember: boolean;
  readonly membershipRole: MembershipRole | null;
  readonly membershipStatus: MembershipStatus | null;
  readonly hasPendingJoinRequest: boolean;
  readonly pendingJoinRequestId: string | null;
}

export interface OrganizationJoinRequestParty {
  readonly identityId: string;
  readonly displayName: string | null;
  readonly email: string | null;
}

export interface OrganizationJoinRequest {
  readonly id: string;
  readonly organizationId: string;
  readonly identityId: string;
  readonly message: string | null;
  readonly status: OrganizationJoinRequestStatus;
  readonly createdAt: string;
  readonly decidedAt: string | null;
  readonly decidedByIdentityId: string | null;
  readonly decisionComment: string | null;
  readonly organization: Organization | null;
  readonly requester: OrganizationJoinRequestParty | null;
}

export interface SubmitOrganizationJoinRequestRequest {
  readonly message?: string;
}

export interface DecideOrganizationJoinRequestRequest {
  readonly decisionComment?: string;
}

// ─── Authorization ────────────────────────────────────────────────────────────

export type InstallationRightType =
  | 'ACQUISITION'
  | 'LEASE'
  | 'CONCESSION'
  | 'TEMPORARY_OCCUPATION'
  | 'PRIVATE_OWNER_CONTRACT';

export type AssetType =
  | 'LAND'
  | 'BUILDING'
  | 'WAREHOUSE'
  | 'OFFICE'
  | 'INDUSTRIAL_ZONE'
  | 'PORT_ZONE'
  | 'OTHER';

export type AssetAvailabilityStatus =
  | 'AVAILABLE'
  | 'RESERVED'
  | 'UNDER_NEGOTIATION'
  | 'ASSIGNED'
  | 'OCCUPIED'
  | 'UNAVAILABLE';

export type InvestOwnerType = 'NWM' | 'PUBLIC_ENTITY' | 'PRIVATE_OWNER' | 'COMPANY' | 'OTHER';

export type InstallationTemplateType = InstallationRightType | 'OTHER';
export type InstallationTemplateStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export type InvestCasePhase =
  | 'INVESTOR_INTAKE'
  | 'COMPANY_REGISTRATION'
  | 'INVESTMENT_PROJECT'
  | 'INSTALLATION_RIGHTS'
  | 'AUTHORIZATION'
  | 'CONSTRUCTION'
  | 'COMPLIANCE'
  | 'EXPLOITATION'
  | 'AFTERCARE'
  | 'CLOSED';

export type InvestCaseStatus =
  | 'DRAFT'
  | 'IN_PROGRESS'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'CLOSED';

export type InvestCasePriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
export type InvestAuthorizationRequestType = 'LAND_SURVEY_REQUEST' | 'BORNAGE' | 'TECHNICAL_DOSSIER' | 'BUILDING_AUTHORIZATION' | 'TEMPORARY_CONNECTION' | 'FINAL_CONNECTION';
export type InvestAuthorizationStatus = 'DRAFT' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'COMPLETED';
export type InvestSiteVisitType = 'CONSTRUCTION_START_NOTIFICATION' | 'SITE_OPENING_VISIT' | 'HSE_VISIT' | 'SITE_MONITORING' | 'RECEPTION_VISIT';
export type InvestSiteVisitStatus = 'SCHEDULED' | 'COMPLETED' | 'CANCELLED';
export type InvestNonConformitySeverity = 'MINOR' | 'MAJOR' | 'CRITICAL';
export type InvestNonConformityStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'WAIVED';
export type InvestConstructionSiteStatus = 'PLANNED' | 'NOTIFIED' | 'OPEN' | 'UNDER_MONITORING' | 'READY_FOR_RECEPTION' | 'COMPLETED' | 'BLOCKED';
export type InvestComplianceStatus = 'DRAFT' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'ISSUED';
export type InvestExploitationAuthorizationStatus = 'DRAFT' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'ACTIVE';
export type InvestTicketCategory = 'maintenance' | 'security' | 'utilities' | 'waste' | 'transport' | 'access' | 'administrative' | 'other';
export type InvestTicketStatus = 'OPEN' | 'ASSIGNED' | 'IN_PROGRESS' | 'WAITING_INVESTOR' | 'RESOLVED' | 'CLOSED' | 'CANCELLED';

export interface Investor {
  readonly id: string;
  readonly organizationId: string;
  readonly externalRef: string | null;
  readonly fullName: string;
  readonly nationality: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface Company {
  readonly id: string;
  readonly organizationId: string;
  readonly legalName: string;
  readonly registrationNumber: string | null;
  readonly taxId: string | null;
  readonly headquartersAddress: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface InvestmentProject {
  readonly id: string;
  readonly organizationId: string;
  readonly companyId: string;
  readonly code: string | null;
  readonly title: string;
  readonly sector: string | null;
  readonly estimatedBudget: number | null;
  readonly currency: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface LandParcel {
  readonly id: string;
  readonly organizationId: string;
  readonly zoneId: string | null;
  readonly displayName: string | null;
  readonly parcelNumber: string;
  readonly titleNumber: string | null;
  readonly areaSqm: number | null;
  readonly locationLabel: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface InvestZone {
  readonly id: string;
  readonly organizationId: string;
  readonly zoneCode: string | null;
  readonly displayName: string;
  readonly zoneType: string | null;
  readonly locationLabel: string | null;
  readonly areaSqm: number | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface InvestBuilding {
  readonly id: string;
  readonly organizationId: string;
  readonly zoneId: string | null;
  readonly landParcelId: string | null;
  readonly buildingCode: string | null;
  readonly displayName: string;
  readonly address: string | null;
  readonly floorCount: number | null;
  readonly areaSqm: number | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface InvestOwnershipDocument {
  readonly id: string;
  readonly organizationId: string;
  readonly ownerId: string;
  readonly assetId: string | null;
  readonly documentType: string;
  readonly fileName: string;
  readonly mimeType: string;
  readonly size: number;
  readonly storageKey: string;
  readonly uploadedByIdentityId: string | null;
  readonly uploadedAt: string;
  readonly isVerified: boolean;
  readonly verifiedAt: string | null;
  readonly verifiedByIdentityId: string | null;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface InvestOwner {
  readonly id: string;
  readonly organizationId: string;
  readonly ownerType: InvestOwnerType;
  readonly companyId: string | null;
  readonly displayName: string;
  readonly identifier: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly address: string | null;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}


export type GrantSource = 'MANUAL' | 'GOVERNANCE';

export interface AuthorizationRole {
  readonly id:            string;
  readonly applicationId: string;
  readonly code:          string;
  readonly displayName:   string;
  readonly description:   string;
  readonly createdAt:     string;
  readonly updatedAt:     string;
}

export interface Permission {
  readonly id:            string;
  readonly applicationId: string;
  readonly code:          string;
  readonly displayName:   string;
  readonly description:   string;
  readonly createdAt:     string;
  readonly updatedAt:     string;
}

export interface RolePermission {
  readonly id:             string;
  readonly applicationId:  string;
  readonly roleCode:       string;
  readonly permissionCode: string;
  readonly grantedAt:      string;
}

export interface AuthorizationGrant {
  readonly id:                  string;
  readonly organizationId:      string;
  readonly applicationId:       string | null;
  readonly identityId:          string;
  readonly roleCode:            string;
  readonly grantedByIdentityId: string;
  readonly source:              GrantSource;
  readonly createdAt:           string;
  readonly expiresAt:           string | null;
  readonly revokedAt:           string | null;
}

export interface OrganizationGrantResult {
  readonly organizationId: string;
  readonly applicationId: string;
  readonly roleCode: string;
  readonly grantedCount: number;
  readonly grants: readonly AuthorizationGrant[];
}

export interface EffectiveAccessResult {
  readonly organizationId:  string;
  readonly identityId:      string;
  readonly applicationId?:  string;
  readonly roles?:          readonly string[];
  readonly permissions?:    readonly string[];
  readonly grants?:         readonly EffectiveAccessGrant[];
  readonly applications?:   readonly ApplicationEffectiveAccess[];
}

export interface EffectiveAccessGrant {
  readonly grantId:   string;
  readonly roleCode:  string;
  readonly source:    string;
  readonly createdAt: string;
  readonly expiresAt: string | null;
}

export interface ApplicationEffectiveAccess {
  readonly applicationId: string;
  readonly roles:         readonly string[];
  readonly permissions:   readonly string[];
  readonly grants:        readonly EffectiveAccessGrant[];
}

// ─── Registered Application ───────────────────────────────────────────────────

export type ApplicationClassification = 'INTERNAL' | 'SHARED' | 'PUBLIC' | 'FLOW' | 'COMMUNITY' | 'INVEST';

export interface RegisteredApplication {
  readonly id:             string;
  readonly organizationId: string | null;
  readonly clientId:       string;
  readonly displayName:    string;
  readonly description:    string | null;
  readonly ownerTeam:      string | null;
  readonly classification: ApplicationClassification;
  readonly redirectUris:   readonly string[];
  readonly isActive:       boolean;
  readonly createdAt:      string;
  readonly updatedAt:      string;
}

export interface RegisterApplicationRequest {
  readonly clientId: string;
  readonly displayName: string;
  readonly description?: string;
  readonly ownerTeam?: string;
  readonly classification: ApplicationClassification;
  readonly redirectUris: readonly string[];
}

// ─── Access Governance ────────────────────────────────────────────────────────

export type AccessRequestStatus = 'SUBMITTED' | 'APPROVED' | 'REJECTED';

export interface AccessRequest {
  readonly id:                    string;
  readonly organizationId:        string;
  readonly applicationId:         string;
  readonly requestedByIdentityId: string;
  readonly targetIdentityId:      string;
  readonly roleCode:              string;
  readonly justification:         string;
  readonly status:                AccessRequestStatus;
  readonly requestedUntil:        string | null;
  readonly createdAt:             string;
  readonly decidedAt:             string | null;
  readonly decidedByIdentityId:   string | null;
  readonly decisionComment:       string | null;
}

// ─── Audit ────────────────────────────────────────────────────────────────────

export interface AuditRecord {
  readonly id:             string;
  readonly category:       string;
  readonly action:         string;
  readonly entityType:     string;
  readonly entityId:       string;
  readonly actorId:        string;
  readonly organizationId: string | null;
  readonly correlationId:  string;
  readonly sourceIp:       string | null;
  readonly payload:        Record<string, unknown>;
  readonly previousHash:   string;
  readonly entryHash:      string;
  readonly recordedAt:     string;
}

export interface AuditExportResponse {
  readonly records:    readonly AuditRecord[];
  readonly nextCursor: string | null;
}

export interface AuditIntegrityResponse {
  readonly verified:       boolean;
  readonly totalRecords:   number;
  readonly firstRecordId:  string | null;
  readonly lastRecordId:   string | null;
  readonly brokenAt:       string | null;
  readonly brokenEntryId:  string | null;
}

// ─── Admin BFF ────────────────────────────────────────────────────────────────

export interface AdminSummary {
  readonly identities:     number;
  readonly organizations:  number;
  readonly applications:   number;
  readonly roles:          number;
  readonly permissions:    number;
  readonly grants:         number;
  readonly accessRequests: number;
  readonly auditRecords:   number;
}

export interface IdentityAccessView {
  readonly identity:       IdentityProfile;
  readonly memberships:    readonly OrganizationMembership[];
  readonly effectiveAccess: EffectiveAccessResult | null;
}

export interface OrganizationSnapshot {
  readonly organization: Organization;
  readonly memberships:  readonly OrganizationMembership[];
  readonly units:        readonly OrganizationUnit[];
}

export interface AccessibleApplication {
  readonly applicationId: string;
  readonly clientId: string;
  readonly displayName: string;
  readonly description: string | null;
  readonly classification: ApplicationClassification;
  readonly roles: readonly string[];
  readonly permissions: readonly string[];
}

export interface AccessibleApplicationsResponse {
  readonly activeOrganizationId: string | null;
  readonly applications: readonly AccessibleApplication[];
}

// ─── Auth / Organizations ─────────────────────────────────────────────────────

export interface UserOrganizationEntry {
  readonly organizationId:   string;
  readonly slug:             string;
  readonly displayName:      string;
  readonly ice:              string | null;
  readonly codeDouane:       string | null;
  readonly codeRc:           string | null;
  readonly codeCentreRc:     number;
  readonly identificationFiscale: string | null;
  readonly isBlocked:        boolean;
  readonly motifBlocage:     string | null;
  readonly dateCreation:     string | null;
  readonly dateEnregistrement: string | null;
  readonly dateBlocage:      string | null;
  readonly email:            string | null;
  readonly telephone:        string | null;
  readonly address:          string | null;
  readonly formeJuridique:   string | null;
  readonly isAgentMaritime:  boolean;
  readonly membershipId:     string;
  readonly membershipRole:   MembershipRole;
  readonly membershipStatus: MembershipStatus;
  readonly joinedAt:         string | null;
  readonly isPreferred:      boolean;
}

export interface UserOrganizationsResponse {
  readonly subjectId:              string;
  readonly activeOrganizationId:   string | null;
  readonly preferredOrganizationId: string | null;
  readonly organizations:          readonly UserOrganizationEntry[];
}

export interface SetActiveOrganizationResponse {
  readonly subjectId:              string;
  readonly activeOrganizationId:   string;
  readonly preferredOrganizationId: string | null;
  readonly organization: {
    readonly organizationId: string;
    readonly slug:           string;
    readonly displayName:    string;
    readonly ice:            string | null;
    readonly codeDouane:     string | null;
    readonly codeRc:         string | null;
    readonly codeCentreRc:   number;
    readonly identificationFiscale: string | null;
    readonly isBlocked:      boolean;
    readonly motifBlocage:   string | null;
    readonly dateCreation:   string | null;
    readonly dateEnregistrement: string | null;
    readonly dateBlocage:    string | null;
    readonly email:          string | null;
    readonly telephone:      string | null;
    readonly address:        string | null;
    readonly formeJuridique: string | null;
    readonly isAgentMaritime: boolean;
  };
  readonly membership: {
    readonly membershipId: string;
    readonly role:         MembershipRole;
    readonly status:       MembershipStatus;
    readonly joinedAt:     string | null;
  };
}

// ─── Auth / Session ───────────────────────────────────────────────────────────

export interface MeResponse {
  readonly subjectId:               string;
  readonly actorId:                 string;
  readonly issuer:                  string;
  readonly email:                   string | undefined;
  readonly username:                string | undefined;
  readonly displayName:             string | undefined;
  readonly organizationId:          string | undefined;
  readonly preferredOrganizationId: string | null;
  readonly activeOrganizationId:    string | null;
  readonly roles:                   readonly string[];
  readonly permissions:             readonly string[];
  readonly scopes:                  readonly string[];
  readonly identity:                IdentityProfile | null;
}


